import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LazyReportSection } from '../hooks/useLazySection';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import type { BingDateGrain } from '../api/bingDashboard';
import { snapchatDashboardApi, type SnapchatReportParams, type SnapchatReportRow, type SnapchatReportType } from '../api/snapchatDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { MultiSelect } from '../components/MultiSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { SortableHeader, compareSortValues, type SortState } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';
import VooglamSnapchatDeliveryDashboard from './VooglamSnapchatDeliveryDashboard';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface MetricField {
  key: string;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

interface DimensionField {
  key: string;
  label: string;
}

interface FilterState {
  target_type: string[];
  new_old_type: string[];
  ad_form_type: string[];
  bid_type: string[];
  material_type: string[];
  directory_type: string[];
  customer_type: string[];
  price_type: string[];
  audience_type: string[];
  loading_page_type: string[];
  account_name: string;
  campaign_name: string;
  adset_name: string;
  ad_name: string;
}

interface AppliedTopState {
  startDate: string;
  endDate: string;
  dateGrain: BingDateGrain;
}

interface SectionDef {
  report: SnapchatReportType;
  title: string;
  dims: string[];
  hasDetailTabs?: boolean;
}

interface TreeMetricAccumulator {
  budget_base: number;
  budget_share: number;
  spend: number;
  conversion_purchases_value: number;
  impressions: number;
  swipes: number;
  conversion_add_cart: number;
  conversion_start_checkout: number;
  conversion_purchases: number;
  conversion_sign_ups: number;
}

interface TreeNode {
  id: string;
  depth: number;
  dimKey: string;
  value: string;
  metrics: TreeMetricAccumulator;
  children: TreeNode[];
  childMap: Map<string, TreeNode>;
}

const SUPPORTED_REGIONS = ['us'];

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const DIMENSIONS: DimensionField[] = [
  { key: 'date', label: '日期维度' },
  { key: 'target_type', label: '目标' },
  { key: 'new_old_type', label: '新老客' },
  { key: 'ad_form_type', label: '广告形式' },
  { key: 'bid_type', label: '出价方式' },
  { key: 'material_type', label: '素材类型' },
  { key: 'directory_type', label: '目录类型' },
  { key: 'price_type', label: '价格带' },
  { key: 'audience_type', label: '受众类型' },
  { key: 'loading_page_type', label: '着陆页' },
  { key: 'customer_type', label: '客户类型' },
  { key: 'account_name', label: '广告账户' },
  { key: 'campaign_name', label: '广告系列名称' },
  { key: 'adset_name', label: '广告组名称' },
  { key: 'ad_name', label: '广告名称' },
];

const DETAIL_PRESETS: Array<{ value: string; label: string; dims: string[]; report: SnapchatReportType }> = [
  { value: 'main', label: '明细报表', dims: ['date'], report: 'detail' },
  { value: 'date', label: '明细报表_日期', dims: ['date'], report: 'detail' },
];

const SECTIONS: SectionDef[] = [
  { report: 'overview', title: '总览报表', dims: ['date'] },
  { report: 'new_old', title: '新老客报表', dims: ['new_old_type', 'date'] },
  { report: 'detail', title: '明细报表', dims: DETAIL_PRESETS[0].dims, hasDetailTabs: true },
];

const EMPTY_FILTERS: FilterState = {
  target_type: [],
  new_old_type: [],
  ad_form_type: [],
  bid_type: [],
  material_type: [],
  directory_type: [],
  customer_type: [],
  price_type: [],
  audience_type: [],
  loading_page_type: [],
  account_name: '',
  campaign_name: '',
  adset_name: '',
  ad_name: '',
};

type EnumFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string[] ? K : never;
}[keyof FilterState];

type TextFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string ? K : never;
}[keyof FilterState];

const DETAIL_FILTER_OPTIONS: Array<{ key: EnumFilterKey; label: string; options: string[]; width?: number }> = [
  { key: 'target_type', label: '目标', options: ['上层总', '下层总', '中层总'] },
  { key: 'new_old_type', label: '新老客', options: ['新客', '老客'] },
  { key: 'bid_type', label: '出价方式', options: ['CPA', 'roas', '价值', '其他', '转化'] },
  { key: 'ad_form_type', label: '广告形式', options: ['其他', 'smart+', '自建视频', '红人spark', '主页spark'] },
  { key: 'material_type', label: '素材类型', options: ['其他', '图片', '横版视频', '竖版视频', 'smart+'] },
  { key: 'directory_type', label: '目录类型', options: ['所有目录', '常青款目录', '优秀目录', '其他'] },
  { key: 'price_type', label: '价格带', options: ['7美金', 'sale', '正价', '原价', '其他'] },
  { key: 'audience_type', label: '受众类型', options: ['九大人群', '眼镜包', '其他'] },
  { key: 'loading_page_type', label: '着陆页', options: ['116列表', '273列表', '274列表', '428列表', '455列表', 'SPU搜索页', '专题页', '详情页', '其他', '117列表', '378列表'], width: 180 },
  { key: 'customer_type', label: '客户类型', options: ['首单', '小排', '排客户列表', '排小排首单老客', '其他'], width: 170 },
];

const DETAIL_TEXT_FILTERS: Array<{ key: TextFilterKey; label: string; width?: number }> = [
  { key: 'account_name', label: '广告账户', width: 190 },
  { key: 'campaign_name', label: '广告系列名称', width: 220 },
  { key: 'adset_name', label: '广告组名称', width: 220 },
  { key: 'ad_name', label: '广告名称', width: 220 },
];

const fmtNum = (v: number, d = 2) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

const METRICS: MetricField[] = [
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'cpm', label: '账户CPM', fmt: fmtNum, permKey: 'cpc' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'swipes', label: '点击次数', fmt: fmtInt },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'spend', label: '账户花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'conversion_add_cart', label: '加购次数', fmt: fmtNum },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'swipe_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'conversion_start_checkout', label: '发起结账次数', fmt: fmtInt },
  { key: 'cost_per_checkout', label: '单次结账费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'swipe_checkout_rate', label: '点击结账率', fmt: fmtPct },
  { key: 'conversion_purchases', label: '购物次数', fmt: fmtNum },
  { key: 'conversion_purchases_value', label: '购物转化价值', fmt: fmtNum, permKey: 'revenue' },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'account_roi', label: '账户ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'swipe_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'aov', label: '账户单均价', fmt: fmtNum, permKey: 'revenue' },
  { key: 'conversion_sign_ups', label: '完成注册次数', fmt: fmtInt },
  { key: 'cost_per_sign_up', label: '单次注册费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'swipe_sign_up_rate', label: '点击注册率', fmt: fmtPct },
  { key: 'purchase_conversion_rate', label: '购物车转化率', fmt: fmtPct },
];

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function initialAppliedTopState(): AppliedTopState {
  return {
    startDate: daysAgo(6),
    endDate: today(),
    dateGrain: 'day',
  };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function cleanParams<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value != null) out[key] = value;
  });
  return out as T;
}

function dimLabel(key: string) {
  return DIMENSIONS.find(d => d.key === key)?.label ?? key;
}

function metricAccumulator(): TreeMetricAccumulator {
  return {
    budget_base: 0,
    budget_share: 0,
    spend: 0,
    conversion_purchases_value: 0,
    impressions: 0,
    swipes: 0,
    conversion_add_cart: 0,
    conversion_start_checkout: 0,
    conversion_purchases: 0,
    conversion_sign_ups: 0,
  };
}

function addMetrics(target: TreeMetricAccumulator, source: Record<string, number> = {}) {
  target.budget_base += source.budget_base ?? 0;
  target.budget_share += source.budget_share ?? 0;
  target.spend += source.spend ?? 0;
  target.conversion_purchases_value += source.conversion_purchases_value ?? 0;
  target.impressions += source.impressions ?? 0;
  target.swipes += source.swipes ?? 0;
  target.conversion_add_cart += source.conversion_add_cart ?? 0;
  target.conversion_start_checkout += source.conversion_start_checkout ?? 0;
  target.conversion_purchases += source.conversion_purchases ?? 0;
  target.conversion_sign_ups += source.conversion_sign_ups ?? 0;
}

function metricFromAccumulator(acc: TreeMetricAccumulator, key: string, totalSpend = acc.budget_base) {
  switch (key) {
    case 'budget_share':
      return totalSpend ? acc.spend / totalSpend : acc.budget_share;
    case 'cpm':
      return acc.impressions ? (acc.spend / acc.impressions) * 1000 : 0;
    case 'account_roi':
      return acc.spend ? acc.conversion_purchases_value / acc.spend : 0;
    case 'cpa':
      return acc.conversion_purchases ? acc.spend / acc.conversion_purchases : 0;
    case 'aov':
      return acc.conversion_purchases ? acc.conversion_purchases_value / acc.conversion_purchases : 0;
    case 'ctr':
      return acc.impressions ? acc.swipes / acc.impressions : 0;
    case 'cpc':
      return acc.swipes ? acc.spend / acc.swipes : 0;
    case 'swipe_purchase_rate':
      return acc.swipes ? acc.conversion_purchases / acc.swipes : 0;
    case 'cost_per_add_to_cart':
      return acc.conversion_add_cart ? acc.spend / acc.conversion_add_cart : 0;
    case 'swipe_add_to_cart_rate':
      return acc.swipes ? acc.conversion_add_cart / acc.swipes : 0;
    case 'cost_per_checkout':
      return acc.conversion_start_checkout ? acc.spend / acc.conversion_start_checkout : 0;
    case 'swipe_checkout_rate':
      return acc.swipes ? acc.conversion_start_checkout / acc.swipes : 0;
    case 'cost_per_sign_up':
      return acc.conversion_sign_ups ? acc.spend / acc.conversion_sign_ups : 0;
    case 'swipe_sign_up_rate':
      return acc.swipes ? acc.conversion_sign_ups / acc.swipes : 0;
    case 'purchase_conversion_rate':
      return acc.conversion_add_cart ? acc.conversion_purchases / acc.conversion_add_cart : 0;
    default:
      return acc[key as keyof TreeMetricAccumulator] ?? 0;
  }
}

function rowToAccumulator(row: SnapchatReportRow | null): TreeMetricAccumulator {
  const acc = metricAccumulator();
  if (row) addMetrics(acc, row.metrics);
  return acc;
}

function buildTree(rows: SnapchatReportRow[], dims: string[]) {
  const root: TreeNode = { id: 'root', depth: -1, dimKey: '', value: '', metrics: metricAccumulator(), children: [], childMap: new Map() };
  rows.forEach(row => {
    addMetrics(root.metrics, row.metrics);
    let parent = root;
    dims.forEach((dim, depth) => {
      const value = row.dimensions?.[dim] || '-';
      const id = `${parent.id}/${dim}:${value}`;
      let node = parent.childMap.get(id);
      if (!node) {
        node = { id, depth, dimKey: dim, value, metrics: metricAccumulator(), children: [], childMap: new Map() };
        parent.childMap.set(id, node);
        parent.children.push(node);
      }
      addMetrics(node.metrics, row.metrics);
      parent = node;
    });
  });
  return root.children;
}

function visibleTreeRows(nodes: TreeNode[], expanded: Set<string>) {
  const out: TreeNode[] = [];
  const walk = (items: TreeNode[]) => {
    items.forEach(node => {
      out.push(node);
      if (node.children.length > 0 && expanded.has(node.id)) walk(node.children);
    });
  };
  walk(nodes);
  return out;
}

function expandableTreeNodeIDs(nodes: TreeNode[]) {
  const ids: string[] = [];
  const walk = (items: TreeNode[]) => items.forEach(node => {
    if (node.children.length > 0) ids.push(node.id);
    walk(node.children);
  });
  walk(nodes);
  return ids;
}

/** Sort metric values recursively inside siblings without flattening the tree. */
function sortTreeSiblings(nodes: TreeNode[], sort: SortState, userLevel: number): TreeNode[] {
  if (!sort) return nodes;
  const metric = METRICS.find(item => item.key === sort.key);
  // Keep masked metrics unsortable so their relative values remain private.
  if (!metric || (metric.permKey && userLevel < 2)) return nodes;

  const ordered = nodes
    .map((node, index) => ({ node, index }))
    .sort((left, right) => {
      const result = compareSortValues(
        metricFromAccumulator(left.node.metrics, metric.key),
        metricFromAccumulator(right.node.metrics, metric.key),
        sort.direction,
      );
      return result === 0 ? left.index - right.index : result;
    });

  return ordered.map(({ node }) => (
    node.children.length === 0
      ? node
      : { ...node, children: sortTreeSiblings(node.children, sort, userLevel) }
  ));
}

function detailReportForDims(_dims: string[]): SnapchatReportType {
  return 'detail';
}

function normalizeDetailDims(dims: string[]) {
  return dims.length > 0 ? dims : ['date'];
}

function EnumFilter({
  label,
  field,
  fallbackOptions,
  value,
  onChange,
  width = 150,
}: {
  label: string;
  field: string;
  fallbackOptions: string[];
  value: string[];
  onChange: (v: string[]) => void;
  width?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: width }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</label>
      <ReportLabelSelect dataset="snapchat_delivery" field={field} pageFields={DETAIL_FILTER_OPTIONS.map(item => item.key)} fallbackOptions={fallbackOptions} value={value} onChange={onChange} width={width} />
    </div>
  );
}

function serializeFilters(filters: FilterState) {
  const out: Record<string, string> = {};
  (Object.keys(filters) as Array<keyof FilterState>).forEach(key => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) out[key] = value.join(',');
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  });
  return out;
}

function FilterInput({ label, value, onChange, width = 150 }: { label: string; value: string; onChange: (v: string) => void; width?: number }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: width }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</span>
      <input className="form-input" placeholder="请选择" value={value} onChange={e => onChange(e.target.value)} style={{ height: 34, width: '100%' }} />
    </label>
  );
}

function DimensionPicker({ selected, onChange }: { selected: string[]; onChange: (next: string[]) => void }) {
  return (
    <div className="dimension-select-row" style={{ display: 'flex' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={DIMENSIONS.map(dim => ({ value: dim.key, label: dim.label }))}
        value={DIMENSIONS.filter(dim => selected.includes(dim.key)).map(dim => dim.key)}
        onChange={onChange}
        countLabelFormatter={count => `已选 ${count} 项`}
        tagDisplay
        style={{ width: 420 }}
      />
    </div>
  );
}

function SnapchatSectionTable({
  section,
  baseParams,
  queryVersion,
  hasPendingTopChanges,
  onQueryAll,
  userLevel,
}: {
  section: SectionDef;
  baseParams: Omit<SnapchatReportParams, 'report' | 'dimensions'> | null;
  queryVersion: number;
  hasPendingTopChanges: boolean;
  onQueryAll: () => void;
  userLevel: number;
}) {
  const [detailPreset, setDetailPreset] = useState('main');
  const [dims, setDims] = useState<string[]>(section.dims);
  const [detailReport, setDetailReport] = useState<SnapchatReportType>(section.report);
  const [appliedDims, setAppliedDims] = useState<string[]>(section.dims);
  const [appliedDetailReport, setAppliedDetailReport] = useState<SnapchatReportType>(section.report);
  const [draftFilters, setDraftFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [appliedTopQueryVersion, setAppliedTopQueryVersion] = useState(queryVersion);
  const [detailQueryVersion, setDetailQueryVersion] = useState(0);
  const [rows, setRows] = useState<SnapchatReportRow[]>([]);
  const [summary, setSummary] = useState<SnapchatReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportQuery, setExportQuery] = useState<SnapchatReportParams | null>(null);
  const [exportDims, setExportDims] = useState<string[]>(section.dims);
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>(null);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`snapchat_dashboard_hidden_cols_${section.report}`);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore corrupted local preference
    }
    return new Set();
  });

  const activeDims = section.hasDetailTabs ? appliedDims : section.dims;
  const hasExpandControls = section.report !== 'overview';
  const treeRoots = useMemo(() => buildTree(rows, activeDims), [activeDims, rows]);
  const sortedTreeRoots = useMemo(() => sortTreeSiblings(treeRoots, sort, userLevel), [sort, treeRoots, userLevel]);
  const visibleRows = useMemo(() => visibleTreeRows(sortedTreeRoots, expanded), [expanded, sortedTreeRoots]);
  const expandableNodeIDs = useMemo(() => expandableTreeNodeIDs(treeRoots), [treeRoots]);
  const visibleMetrics = useMemo(() => METRICS.filter(metric => !hiddenMetricKeys.has(metric.key)), [hiddenMetricKeys]);
  const metricToggleCols = useMemo(() => METRICS.map(metric => ({ key: metric.key, label: metric.label })), []);

  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const currentReportParams = useMemo(() => {
    if (!baseParams) return null;
    return cleanParams({
      ...baseParams,
      ...(section.hasDetailTabs ? serializeFilters(appliedFilters) : {}),
      report: section.hasDetailTabs ? appliedDetailReport : section.report,
      dimensions: activeDims.join(','),
    });
  }, [activeDims, appliedDetailReport, appliedFilters, baseParams, section.hasDetailTabs, section.report]);

  const toggleMetricHidden = (key: string) => {
    setSort(current => current?.key === key ? null : current);
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(`snapchat_dashboard_hidden_cols_${section.report}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const resetMetricHidden = () => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(`snapchat_dashboard_hidden_cols_${section.report}`); } catch {}
  };

  const fetchData = useCallback(async () => {
    if (!currentReportParams) {
      setExportQuery(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await snapchatDashboardApi.getReport(currentReportParams);
      setRows(res.data?.list ?? []);
      setSummary(res.data?.summary ?? null);
      setExpanded(new Set());
      setExportQuery(currentReportParams);
      setExportDims(activeDims);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setExportQuery(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentReportParams]);

  useEffect(() => {
    if (appliedTopQueryVersion === queryVersion) return;
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    setAppliedFilters(draftFilters);
    setAppliedTopQueryVersion(queryVersion);
  }, [appliedTopQueryVersion, detailReport, dims, draftFilters, queryVersion]);

  useEffect(() => {
    if (appliedTopQueryVersion !== queryVersion) return;
    fetchData();
  }, [appliedTopQueryVersion, detailQueryVersion, fetchData, queryVersion]);

  const setEnumDetailFilter = (key: EnumFilterKey, value: string[]) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  const setTextDetailFilter = (key: TextFilterKey, value: string) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  useMarketingViewSection('detail:' + section.report, {
    label: section.title, state: { detailPreset, dims, detailReport, draftFilters, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setDetailPreset(value.detailPreset); setDims(value.dims); setDetailReport(value.detailReport); setDraftFilters(value.draftFilters); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading,
  });

  const queryDetail = () => {
    setAppliedFilters(draftFilters);
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    if (hasPendingTopChanges) onQueryAll();
    else setDetailQueryVersion(version => version + 1);
  };
  const resetDetail = () => {
    setDetailPreset('main');
    setDims(section.dims); setAppliedDims(section.dims);
    setDetailReport(section.report); setAppliedDetailReport(section.report);
    setDraftFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS);
    setDetailQueryVersion(version => version + 1);
  };
  const applyDetailDimensions = (next: string[]) => {
    const normalized = normalizeDetailDims(next);
    const nextReport = detailReportForDims(normalized);
    setDims(normalized); setAppliedDims(normalized);
    setDetailReport(nextReport); setAppliedDetailReport(nextReport);
  };
  const renderMetric = (acc: TreeMetricAccumulator, metric: MetricField) => {
    const totalSpend = summary?.metrics?.spend ?? 0;
    const raw = metric.fmt(metricFromAccumulator(acc, metric.key, totalSpend));
    if (!metric.permKey) return raw;
    return sf(raw, metric.permKey, userLevel).value ?? '锁定';
  };

  const renderSummaryMetric = (metric: MetricField) => {
    const acc = rowToAccumulator(summary);
    return renderMetric(acc, metric);
  };

  const handleExport = useCallback(async () => {
    if (!exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_snapchat_report',
        title: `Snapchat投放看板-${section.title}`,
        file_name: `Snapchat投放看板_${section.title}_${exportQuery.start_date}_${exportQuery.end_date}.csv`,
        brand_id: exportQuery.brand_id,
        site_id: exportQuery.site_id,
        params: { ...exportQuery },
        columns: [
          ...exportDims.map(dim => ({ key: dim, label: dimLabel(dim) })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [enqueueReportExport, exportDims, exportQuery, section.title, showToast, visibleMetrics]);

  const toggleNode = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(expandableNodeIDs));
  const collapseAll = () => setExpanded(new Set());

  return (
    <section style={{
      marginTop: 16,
      background: 'var(--surface)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{section.title}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {section.report !== 'overview' && <>
            <button type="button" className="btn btn-outline btn-sm" onClick={expandAll} disabled={loading || expandableNodeIDs.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Maximize2 size={14} strokeWidth={2} />全部展开</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={collapseAll} disabled={loading || expanded.size === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Minimize2 size={14} strokeWidth={2} />全部收起</button>
          </>}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleExport}
            disabled={loading || !exportQuery}
            title="导出当前报表"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} strokeWidth={2} />
            导出
          </button>
          <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
        </div>
      </div>

      {section.hasDetailTabs && (
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid var(--border2)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            {DETAIL_FILTER_OPTIONS.map(item => (
              <EnumFilter
                key={item.key}
                label={item.label}
                field={item.key}
                fallbackOptions={item.options}
                value={draftFilters[item.key]}
                width={item.width}
                onChange={v => setEnumDetailFilter(item.key, v)}
              />
            ))}
            {DETAIL_TEXT_FILTERS.map(item => (
              <FilterInput
                key={item.key}
                label={item.label}
                value={draftFilters[item.key]}
                width={item.width}
                onChange={v => setTextDetailFilter(item.key, v)}
              />
            ))}
            <DashboardQueryActions loading={loading} onQuery={queryDetail} onReset={resetDetail} queryLabel="查询" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="page-tabs" style={{ marginBottom: 0 }}>
              {DETAIL_PRESETS.map(item => (
                <div
                  key={item.value}
                  className={`page-tab${detailPreset === item.value ? ' active' : ''}`}
                  onClick={() => {
                    setDetailPreset(item.value);
                    applyDetailDimensions(item.dims);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
            <DimensionPicker
              selected={dims}
              onChange={next => {
                applyDetailDimensions(next);
              }}
            />
          </div>
        </div>
      )}

      {error && <div style={{ margin: '12px 16px 0', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      <div className="table-wrap" style={{ position: 'relative', maxHeight: section.hasDetailTabs ? 620 : 420, overflow: 'auto', borderTop: section.hasDetailTabs ? 0 : '1px solid var(--border2)' }}>
        <table style={{ minWidth: Math.max(1280, activeDims.length * 120 + visibleMetrics.length * 95) }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            <tr>
              <th style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: '1%', minWidth: 160, maxWidth: 360, boxShadow: '2px 0 4px rgba(15,23,42,.06)' } : { whiteSpace: 'nowrap', background: 'var(--surface2)', minWidth: 240 }}>
                {hasExpandControls ? <DimensionHeaderTooltip labels={(section.hasDetailTabs ? DIMENSIONS.map(dim => dim.key) : activeDims).map(dim => dimLabel(dim))} /> : '汇总'}
              </th>
              {visibleMetrics.map(metric => (
                <SortableHeader
                  key={metric.key}
                  sortKey={metric.key}
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  disabled={Boolean(metric.permKey && userLevel < 2)}
                  style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}
                >
                  <MetricDescriptionTooltip label={metric.label} />
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary && (
              <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', whiteSpace: 'nowrap', color: 'var(--text2)' } : { whiteSpace: 'nowrap', color: 'var(--text2)' }}>总计</td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderSummaryMetric(metric)}</td>
                ))}
              </tr>
            )}
            {loading && visibleRows.length === 0 ? (
              <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            ) : !baseParams ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>请在左侧切换到支持的 Snapchat 站点</td></tr>
            ) : visibleRows.length === 0 ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : visibleRows.map((node, index) => (
              <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', width: '1%', minWidth: 160, maxWidth: 360 } : { minWidth: 240, whiteSpace: node.dimKey === 'campaign_name' ? 'normal' : 'nowrap' }}>
                  <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                    {node.children.length > 0 && (
                      <button type="button" className="tree-toggle-btn" onClick={() => toggleNode(node.id)} aria-label={expanded.has(node.id) ? '收起' : '展开'} aria-expanded={expanded.has(node.id)}>
                        {expanded.has(node.id) ? '−' : '+'}
                      </button>
                    )}
                    {(node.children.length > 0 || node.depth > 0) && <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{dimLabel(node.dimKey).replace(/维度$/, '')}</span>}
                    {hasExpandControls ? <TruncatedValueTooltip value={node.value} /> : <span>{node.value}</span>}
                  </span>
                </td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {renderMetric(node.metrics, metric)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && visibleRows.length > 0 && <TableLoadingOverlay />}
      </div>
    </section>
  );
}

function ZeeloolSnapchatDeliveryDashboard() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedTop, setAppliedTop] = useState<AppliedTopState>(() => initialAppliedTopState());
  const [queryVersion, setQueryVersion] = useState(0);
  const initialQueryKeyRef = useRef('');

  const currentSite = useMemo(() => {
    if (brand !== 'zeelool') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site) return null;
    const region = regionFromSiteCode(site.code);
    if (!SUPPORTED_REGIONS.includes(region)) return null;
    return { region, siteId: site.id, siteCode: site.code };
  }, [brand, siteId, sites]);

  const dateRange = useMemo(() => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const baseParams = useMemo(() => {
    if (!brandId || !currentSite?.siteId) return null;
    return cleanParams({
      brand_id: brandId,
      site_id: currentSite.siteId,
      start_date: appliedTop.startDate,
      end_date: appliedTop.endDate,
      date_grain: appliedTop.dateGrain,
    });
  }, [appliedTop, brandId, currentSite?.siteId]);
  const hasPendingTopChanges = dateRange.start !== appliedTop.startDate
    || dateRange.end !== appliedTop.endDate
    || dateGrain !== appliedTop.dateGrain;

  const query = () => {
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  };
  useMarketingViewSection('page', {
    label: '公共日期', state: { dateGrain, datePreset, customStart, customEnd },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); },
    loading: false, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
    summary: dateRange.start + ' ～ ' + dateRange.end,
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(7));
    setCustomEnd(today());
    const range = getPresetRange('7d', daysAgo(7), today());
    setAppliedTop({ startDate: range.start, endDate: range.end, dateGrain: 'day' });
    setQueryVersion(v => v + 1);
  };
  useEffect(() => {
    if (!brandId || !currentSite?.siteId) return;
    const key = `${brandId}:${currentSite.siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  }, [brandId, currentSite?.siteId, dateGrain, dateRange.end, dateRange.start]);

  if (!sitesLoaded) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 0 }}><TableLoadingState /></div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
          <DashboardQueryActions onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {!currentSite && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
          请在左侧切换到 US 站点后查看 Snapchat 投放看板。
        </div>
      )}

      {currentSite && SECTIONS.map(section => (
        <LazyReportSection key={section.report} enabled={Boolean(currentSite)}>
          <SnapchatSectionTable
            section={section}
            baseParams={baseParams}
            queryVersion={queryVersion}
            hasPendingTopChanges={hasPendingTopChanges}
            onQueryAll={query}
            userLevel={userLevel}
          />
        </LazyReportSection>
      ))}
    </div>
  );
}

export default function SnapchatDeliveryDashboard() {
  const brand = useAppStore(state => state.brand);
  return brand === 'vooglam' ? <VooglamSnapchatDeliveryDashboard /> : <ZeeloolSnapchatDeliveryDashboard />;
}
